import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import { Lock, Unlock, Sparkles, BookOpen, Download, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import CommandLayout from "@/components/CommandLayout";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import RefillModal from "@/components/RefillModal";
import LibraryStrategyCard from "@/components/LibraryStrategyCard";

interface ContentItem {
  id: string;
  title: string | null;
  teaser_text: string | null;
  analysis_logic: string | null;
  neutralization_script: string | null;
  category: string | null;
  toxicity_score: number;
  credit_cost: number | null;
  is_free: boolean | null;
}

export default function Library() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { credits, refetch: refetchCredits } = useShieldCredits();
  const [unlocking, setUnlocking] = useState<string | null>(null);
  const [showRefill, setShowRefill] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState<ContentItem | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleDownloadCard = useCallback(async (item: ContentItem) => {
    setActiveCard(item);
    setDownloadingCard(item.id);
    // Wait for render
    await new Promise((r) => setTimeout(r, 200));
    try {
      if (!cardRef.current) throw new Error("Card not ready");
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#09090b",
        scale: 2,
        useCORS: true,
        width: 1080,
        height: 1920,
      });
      const link = document.createElement("a");
      link.download = `greyrockai-${(item.title ?? "strategy").replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Strategy card downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate card");
    } finally {
      setDownloadingCard(null);
    }
  }, []);

  // Fetch all content
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["library-content"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_library")
        .select("id, title, teaser_text, analysis_logic, neutralization_script, category, toxicity_score, credit_cost, is_free")
        .is("user_id", null)
        .order("category")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ContentItem[];
    },
    enabled: !!user,
  });

  // Fetch user's unlocks
  const { data: unlocks = [] } = useQuery({
    queryKey: ["user-unlocks", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_content_unlocks")
        .select("content_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []).map((r) => r.content_id);
    },
    enabled: !!user,
  });

  const unlockedSet = new Set(unlocks);

  const isUnlocked = (id: string) => unlockedSet.has(id);

  const handleUnlock = async (item: ContentItem) => {
    if (!user) return;
    const cost = item.credit_cost ?? 2;

    if (credits < cost) {
      toast.error("Not enough credits", {
        description: `You need ${cost} credits to unlock this content.`,
        action: { label: "Buy credits", onClick: () => setShowRefill(true) },
      });
      setShowRefill(true);
      return;
    }

    setUnlocking(item.id);
    try {
      const { error } = await supabase.rpc("handle_content_unlock", {
        target_content_id: item.id,
        credit_cost: cost,
      });
      if (error) throw error;

      // Refresh credits and unlocks
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["shield-credits", user.id] }),
        queryClient.invalidateQueries({ queryKey: ["account-type", user.id] }),
        queryClient.invalidateQueries({ queryKey: ["user-unlocks", user.id] }),
      ]);
      refetchCredits();
      toast.success("Content unlocked! 🔓");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message?.includes("Insufficient") ? "Not enough credits" : "Failed to unlock content");
    } finally {
      setUnlocking(null);
    }
  };

  // Group by category
  const grouped = items.reduce<Record<string, ContentItem[]>>((acc, item) => {
    const cat = item.category ?? "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  if (!user) {
    return (
      <CommandLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">Please log in to access the Library.</p>
        </div>
      </CommandLayout>
    );
  }

  return (
    <CommandLayout>
      <PageBreadcrumb items={[{ label: "Dashboard", path: "/dashboard" }, { label: "Library" }]} />

      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">
          <span className="text-foreground">Forensic </span>
          <span className="text-gradient-cyan">Library</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Unlock expert analyses, scripts, and neutralization strategies
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading library...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <BookOpen className="w-10 h-10 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">No content available yet. Check back soon!</p>
        </div>
      ) : (
        <div className="space-y-10">
          {Object.entries(grouped).map(([category, catItems]) => (
            <section key={category}>
              <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan" />
                {category}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {catItems.map((item) => {
                  const unlocked = item.is_free || isUnlocked(item.id);
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        "rounded-xl border p-5 flex flex-col gap-3 transition-all",
                        unlocked
                          ? "bg-card border-cyan/20 hover:border-cyan/40"
                          : "bg-card border-border hover:border-border/60"
                      )}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground line-clamp-2">
                          {item.title ?? "Untitled"}
                        </h3>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={cn(
                            "text-[10px] font-mono font-bold px-1.5 py-0.5 rounded",
                            Number(item.toxicity_score) >= 7
                              ? "bg-destructive/20 text-destructive"
                              : Number(item.toxicity_score) >= 4
                                ? "bg-amber-500/20 text-amber-400"
                                : "bg-muted text-muted-foreground"
                          )}>
                            {Number(item.toxicity_score).toFixed(1)}
                          </span>
                          {unlocked ? (
                            <Unlock className="w-4 h-4 text-cyan" />
                          ) : (
                            <Lock className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {/* Teaser */}
                      <p className="text-xs text-muted-foreground line-clamp-3">
                        {item.teaser_text ?? "No preview available."}
                      </p>

                      {/* Gated content */}
                      <div className="space-y-3 flex-1">
                        {unlocked ? (
                          <>
                            {item.analysis_logic && (
                              <div>
                                <p className="text-[10px] uppercase font-semibold tracking-wide text-cyan mb-1">Forensic Analysis</p>
                                <p className="text-xs leading-relaxed text-foreground">{item.analysis_logic}</p>
                              </div>
                            )}
                            {item.neutralization_script && (
                              <div>
                                <p className="text-[10px] uppercase font-semibold tracking-wide text-cyan mb-1">Response Strategy</p>
                                <p className="text-xs leading-relaxed font-mono text-foreground">{item.neutralization_script}</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="relative">
                            <p className="blur-[6px] select-none text-muted-foreground text-xs leading-relaxed">
                              {(item.analysis_logic ?? "Detailed forensic analysis available after unlock...").slice(0, 200)}...
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Action */}
                      {unlocked ? (
                        <div className="mt-auto flex flex-col gap-2">
                          <button
                            onClick={() => navigate("/rehearsal")}
                            className={cn(
                              "w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold",
                              "bg-cyan/10 text-cyan hover:bg-cyan/20 transition-colors"
                            )}
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            Practice in Rehearse Mode
                          </button>
                          {item.analysis_logic && item.neutralization_script && (
                            <button
                              disabled={downloadingCard === item.id}
                              onClick={() => handleDownloadCard(item)}
                              className={cn(
                                "w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold",
                                "bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors",
                                "disabled:opacity-50 disabled:cursor-not-allowed"
                              )}
                            >
                              <Download className="w-3.5 h-3.5" />
                              {downloadingCard === item.id ? "Generating..." : "Download Strategy Card"}
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          disabled={unlocking === item.id}
                          onClick={() => handleUnlock(item)}
                          className={cn(
                            "mt-auto w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold",
                            "bg-secondary text-foreground hover:bg-secondary/80 transition-colors",
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                          )}
                        >
                          <Lock className="w-3.5 h-3.5" />
                          {unlocking === item.id ? "Unlocking..." : `Unlock for ${item.credit_cost ?? 2} Credits`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <RefillModal open={showRefill} onOpenChange={setShowRefill} currentCredits={credits} />

      {/* Hidden card template for export */}
      {activeCard && (
        <LibraryStrategyCard
          ref={cardRef}
          category={activeCard.category ?? "Uncategorized"}
          title={activeCard.title ?? "Untitled"}
          analysisLogic={activeCard.analysis_logic ?? ""}
          neutralizationScript={activeCard.neutralization_script ?? ""}
          toxicityScore={activeCard.toxicity_score}
        />
      )}
    </CommandLayout>
  );
}
